import React, { useState, useEffect } from 'react';
import {
  Box,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Divider,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Build as MaintenanceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API } from '../API/api';
import { useAuth } from '../Context/AuthContext';

// Thème Material pour éviter le conflit avec MUI Joy (pxToRem manquant dans Joy theme)
const materialTheme = createTheme();

const alertLevels = {
  critical: { color: 'error', icon: ErrorIcon, label: 'Critique' },
  warning: { color: 'warning', icon: WarningIcon, label: 'Attention' },
  info: { color: 'info', icon: InfoIcon, label: 'Info' },
  expired: { color: 'error', icon: ErrorIcon, label: 'Expiré' },
};

export default function DocumentAlertsPopup({ sx = {} }) {
  const { authState } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isOpen = Boolean(anchorEl);

  const fetchAlerts = async () => {
    if (!authState?.token) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setAlerts(response.data.data?.alerts || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Impossible de charger les alertes');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && authState?.token) {
      fetchAlerts();
    }
  }, [isOpen, authState?.token]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const totalCount = alerts.length;

  return (
    <ThemeProvider theme={materialTheme}>
      <Box
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          display: 'flex',
          border: 'none',
          outline: 'none',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: '50px',
          bgcolor: '#4285F4',
          color: 'white',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(66, 133, 244, 0.4)',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: '#3367D6',
            boxShadow: '0 6px 24px rgba(66, 133, 244, 0.5)',
          },
        }}
      >
        <NotificationsIcon sx={{ fontSize: 22 }} />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Alertes
        </Typography>
        {totalCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              right: 8,
              minWidth: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: '#EA4335',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'white',
            }}
          >
            {totalCount}
          </Box>
        )}
      </Box>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mb: 1.5,
            minWidth: 340,
            maxWidth: 420,
            maxHeight: 480,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="primary" />
            Alertes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount > 0 ? `${totalCount} alerte(s) à traiter` : 'Aucune alerte'}
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error" sx={{ p: 2 }}>
              {error}
            </Typography>
          ) : alerts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              Aucune alerte pour le moment
            </Typography>
          ) : (
            <List disablePadding>
              {alerts.map((alert, index) => {
                const level = alertLevels[alert.alert_level] || alertLevels.info;
                const Icon = level.icon;
                const TypeIcon = alert.type === 'maintenance' ? MaintenanceIcon : Icon;
                return (
                  <React.Fragment key={alert.id || index}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ py: 1.5, px: 2 }} alignItems="flex-start" dense>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <TypeIcon color={level.color} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {alert.title}
                            </Typography>
                            <Chip label={alert.type === 'document' ? 'Document' : 'Maintenance'} size="small" variant="outlined" />
                            <Chip label={level.label} color={level.color} size="small" />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {alert.related_entity && `${alert.related_entity} • `}
                            {alert.message}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </ThemeProvider>
  );
}
